/**
 * Sequence Missions Module
 * 
 * Contains mission data for tactical sequence missions.
 * Each mission focuses on a particular tactical theme from the tacticalThemeClassifier.
 */

import { Mission, SequenceMissionProps } from '../types/mission';
import { TACTICAL_PATTERNS, getAvailableTacticalThemes, getTacticalThemeDescription } from '../services/chess/tacticalPatternsAdapter';

/**
 * Sequence mission data for the Chess Codebreaker game
 * Each mission challenges the player to identify or execute a tactical sequence
 */
export const sequenceMissions: SequenceMissionProps[] = [
  // Stage 3: Basic Tactical Patterns - Forks
  {
    id: 'sequence-101',
    title: 'Operation Knight Fork',
    description: 'Learn to identify and execute a basic knight fork',
    stage: 3,
    type: 'sequence',
    difficulty: 2,
    timeLimit: 120,
    prerequisites: ['piece-movement-105'],
    briefing: 'Agent, it\'s time to learn one of the most powerful tactical patterns in chess - the fork. A fork occurs when a single piece attacks two or more targets simultaneously, forcing your opponent to respond to multiple threats.',
    objective: 'Execute the correct sequence of moves to set up and deliver a knight fork',
    successCriteria: 'Successfully complete the sequence without mistakes',
    position: TACTICAL_PATTERNS.fork[0].fen,
    sequence: TACTICAL_PATTERNS.fork[0].moves,
    tacticalTheme: 'fork',
    rewards: {
      experience: 200,
      skillPoints: {
        coordinates: 0,
        basicMoves: 1,
        sequences: 3,
        gameAnalysis: 1
      }
    },
    sequenceCompletionCriteria: {
      maxDeviation: 0,
      requiredThemeDetection: true
    }
  },
  {
    id: 'sequence-102',
    title: 'Royal Fork',
    description: 'Set up a knight fork targeting the king and another piece',
    stage: 3,
    type: 'sequence',
    difficulty: 3,
    timeLimit: 150,
    prerequisites: ['sequence-101'],
    briefing: 'Building on your knowledge of forks, this mission focuses on a special type of fork that targets the enemy king, which forces an immediate response. We call this a "royal fork" - one of the most effective tactical weapons in your arsenal.',
    objective: 'Set up and execute a knight fork targeting the enemy king and another piece',
    successCriteria: 'Complete the sequence and identify the tactical theme',
    position: TACTICAL_PATTERNS.fork[1].fen,
    sequence: TACTICAL_PATTERNS.fork[1].moves,
    tacticalTheme: 'fork',
    rewards: {
      experience: 250,
      skillPoints: {
        coordinates: 0,
        basicMoves: 1,
        sequences: 4,
        gameAnalysis: 1
      }
    }
  },
  
  // Stage 3: Basic Tactical Patterns - Pins
  {
    id: 'sequence-103',
    title: 'Operation Absolute Pin',
    description: 'Learn to identify and execute a pin against the enemy king',
    stage: 3,
    type: 'sequence',
    difficulty: 2,
    timeLimit: 120,
    prerequisites: ['sequence-102'],
    briefing: 'Agent, pins are powerful tactical devices that immobilize enemy pieces. An "absolute pin" occurs when a piece cannot legally move because it would expose the king to check, which is illegal.',
    objective: 'Execute the correct sequence to create and exploit an absolute pin',
    successCriteria: 'Successfully complete the sequence and identify the tactical theme',
    position: TACTICAL_PATTERNS.pin[0].fen,
    sequence: TACTICAL_PATTERNS.pin[0].moves,
    tacticalTheme: 'pin',
    rewards: {
      experience: 250,
      skillPoints: {
        coordinates: 0,
        basicMoves: 1,
        sequences: 4,
        gameAnalysis: 1
      }
    }
  },
  {
    id: 'sequence-104',
    title: 'Exploiting the Pin',
    description: 'Exploit a pin to gain material advantage',
    stage: 3,
    type: 'sequence',
    difficulty: 3,
    timeLimit: 150,
    prerequisites: ['sequence-103'],
    briefing: 'Now that you understand the concept of pins, it\'s time to learn how to exploit them. When a piece is pinned, it often becomes vulnerable to capture or additional threats.',
    objective: 'Use a pin to gain a material advantage',
    successCriteria: 'Successfully complete the sequence with the correct tactical theme',
    position: TACTICAL_PATTERNS.pin[2].fen,
    sequence: TACTICAL_PATTERNS.pin[2].moves,
    tacticalTheme: 'pin',
    rewards: {
      experience: 300,
      skillPoints: {
        coordinates: 0,
        basicMoves: 0,
        sequences: 5,
        gameAnalysis: 2
      }
    }
  },
  
  // Stage 3: Basic Tactical Patterns - Discovered Attacks
  {
    id: 'sequence-105',
    title: 'The Hidden Threat',
    description: 'Learn to use discovered attacks to create multiple threats',
    stage: 3,
    type: 'sequence',
    difficulty: 3,
    timeLimit: 180,
    prerequisites: ['sequence-104'],
    briefing: 'Discovered attacks are among the most deceptive tactical patterns in chess. By moving one piece, you reveal an attack from another piece behind it, often creating a double threat that\'s difficult to defend against.',
    objective: 'Execute a discovered attack to create multiple threats',
    successCriteria: 'Complete the sequence without deviating from the solution',
    position: TACTICAL_PATTERNS.discoveredAttack[0].fen,
    sequence: TACTICAL_PATTERNS.discoveredAttack[0].moves,
    tacticalTheme: 'discoveredAttack',
    rewards: {
      experience: 350,
      skillPoints: {
        coordinates: 0,
        basicMoves: 0,
        sequences: 6,
        gameAnalysis: 2
      }
    }
  },
  
  // Stage 4: Advanced Tactical Patterns - Sacrifices
  {
    id: 'sequence-201',
    title: 'The Noble Sacrifice',
    description: 'Learn how to sacrifice material for a tactical advantage',
    stage: 4,
    type: 'sequence',
    difficulty: 4,
    timeLimit: 210,
    prerequisites: ['sequence-105'],
    briefing: 'Agent, one of the most advanced tactical motifs is the sacrifice - deliberately giving up material to gain a positional advantage or initiative. This mission will test your ability to look beyond the immediate material count.',
    objective: 'Sacrifice a piece to gain a decisive advantage',
    successCriteria: 'Execute the sacrifice and follow up correctly',
    position: TACTICAL_PATTERNS.sacrifice[2].fen,
    sequence: TACTICAL_PATTERNS.sacrifice[2].moves,
    tacticalTheme: 'sacrifice',
    rewards: {
      experience: 400,
      skillPoints: {
        coordinates: 0,
        basicMoves: 0,
        sequences: 7,
        gameAnalysis: 3
      }
    },
    sequenceCompletionCriteria: {
      maxDeviation: 0,
      requiredThemeDetection: true
    }
  },
  
  // Stage 4: Advanced Tactical Patterns - Checkmate Combinations
  {
    id: 'sequence-202',
    title: 'Checkmate Sequence',
    description: 'Execute a complex checkmate combination',
    stage: 4,
    type: 'sequence',
    difficulty: 5,
    timeLimit: 300,
    prerequisites: ['sequence-201'],
    briefing: 'This is the ultimate test of your tactical abilities, agent. Checkmate combinations often involve multiple tactical elements - sacrifices, discovered attacks, and more - all working together to trap the enemy king.',
    objective: 'Execute the checkmate sequence correctly',
    successCriteria: 'Deliver checkmate in the specified number of moves',
    position: TACTICAL_PATTERNS.checkmate[0].fen,
    sequence: TACTICAL_PATTERNS.checkmate[0].moves,
    tacticalTheme: 'checkmate',
    secondaryThemes: ['sacrifice', 'discoveredAttack'],
    rewards: {
      experience: 500,
      skillPoints: {
        coordinates: 0,
        basicMoves: 0,
        sequences: 10,
        gameAnalysis: 5
      }
    },
    timePressure: true
  }
];

/**
 * Get all sequence missions
 * 
 * @returns Array of all sequence missions
 */
export const getAllSequenceMissions = (): SequenceMissionProps[] => {
  return [...sequenceMissions];
};

/**
 * Get sequence missions by tactical theme
 * 
 * @param theme Tactical theme to filter by
 * @returns Array of sequence missions with the specified theme
 */
export const getSequenceMissionsByTheme = (theme: string): SequenceMissionProps[] => {
  return sequenceMissions.filter(mission => 
    mission.tacticalTheme === theme || 
    mission.secondaryThemes?.includes(theme)
  );
};

/**
 * Get sequence missions by difficulty
 * 
 * @param minDifficulty Minimum difficulty level (inclusive)
 * @param maxDifficulty Maximum difficulty level (inclusive)
 * @returns Array of sequence missions within the difficulty range
 */
export const getSequenceMissionsByDifficulty = (
  minDifficulty: number,
  maxDifficulty: number
): SequenceMissionProps[] => {
  return sequenceMissions.filter(
    mission => mission.difficulty >= minDifficulty && mission.difficulty <= maxDifficulty
  );
};

/**
 * Get a specific sequence mission by ID
 * 
 * @param missionId ID of the mission to retrieve
 * @returns The sequence mission with the specified ID or undefined if not found
 */
export const getSequenceMission = (missionId: string): SequenceMissionProps | undefined => {
  return sequenceMissions.find(mission => mission.id === missionId);
};

export default {
  sequenceMissions,
  getAllSequenceMissions,
  getSequenceMissionsByTheme,
  getSequenceMissionsByDifficulty,
  getSequenceMission
}; 